import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { login, queryClient, isOfficalAddress, toastError } from '@/utils';
import {
  useCurrentAddressInfo,
  updateUserDetailQuery,
  useFollowMutation,
  useOfficialFollow,
} from '@/hooks';
import { setQueryFeedAllUsersInList } from '@/views/Stream/hooks/list';

import { openFollowLimitModal } from './components/FollowLimitModal/FollowLimitModal';

const followErrorMsgHandler = (err: any) => {
  const errorMsg = String(err) || 'failed';

  if (errorMsg.includes('need a Web3 ID to increase your following limit to')) {
    openFollowLimitModal(errorMsg);
  } else {
    toastError(err || 'failed');
  }
};

const changeUserFollow = (pre?: UnionAccount) => {
  if (pre) {
    pre.is_following = !pre.is_following;
  }
};

export const useUnionFollow = ({
  address,
  isFollowing = false,
  onFollowCallback,
  restParams,
  operateCallback,
}: {
  address: string;
  isFollowing?: boolean;
  restParams?: Record<string, string | number | boolean | undefined>;
  operateCallback?: (isFollowing: boolean) => void;
  onFollowCallback?: (isSuccess: boolean) => void;
}) => {
  const { address: currentAddress, isVerified } = useCurrentAddressInfo();

  const isOfficialAccount = useMemo(() => isOfficalAddress(address), [address]);

  const [_isFollowing, setFollowing] = useState(isFollowing);
  const { mutateAsync, isLoading } = useFollowMutation(address);
  const { mutateAsync: followOfficial, isLoading: isfollowingOfficial } =
    useOfficialFollow(address);

  useEffect(() => {
    setFollowing(isFollowing);
  }, [isFollowing]);

  const handleFollowClick = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (!address || !isVerified) {
        login();
        onFollowCallback?.(false);
        return;
      }

      try {
        const params = { isFollowing: _isFollowing, ...restParams };
        const res = await (isOfficialAccount ? followOfficial : mutateAsync)(params);

        if (!res?.is_success) {
          onFollowCallback?.(false);
          throw new Error('failed');
        }

        onFollowCallback?.(true);
        setFollowing(!_isFollowing);

        if (currentAddress) {
          setQueryFeedAllUsersInList(currentAddress, address, changeUserFollow);
        }

        if (isOfficialAccount) {
          const query = ['/official', String(address), currentAddress];
          const queryData = queryClient.getQueryData<{ official: OfficialAccount }>(query);
          if (queryData) {
            const count = queryData.official.follower_count ?? 0;
            queryClient.setQueryData(query, {
              ...queryData,
              official: {
                ...queryData.official,
                is_following: !isFollowing,
                follower_count: !isFollowing ? count + 1 : count - 1,
              },
            });
          }
        } else {
          updateUserDetailQuery({
            id: address,
            is_following: !_isFollowing,
          });
        }
        operateCallback?.(!_isFollowing);
      } catch (error) {
        onFollowCallback?.(false);
        followErrorMsgHandler(error);
      }
    },
    [
      address,
      isVerified,
      mutateAsync,
      _isFollowing,
      followOfficial,
      isOfficialAccount,
      restParams,
      currentAddress,
      isFollowing,
      operateCallback,
      onFollowCallback,
    ],
  );

  return {
    isLoading: isLoading || isfollowingOfficial,
    followMutate: handleFollowClick,
    isLocalFollowing: isFollowing,
  };
};
